import {
  getTranscriptAnalysisPresentation,
  type TranscriptAnalysisPresentation,
} from "@/lib/transcripts/transcriptAnalysisPresentation";

export interface ParsedTranscriptAnalysisResult {
  quality: string | null;
  analysisReason: string | null;
  transcriptDocumentContext:
    | {
        sourceFormat?: string;
        documentKind?: string;
        interactionMode?: string;
        speakerDetectionConfidence?: string;
        metadata?: {
          title?: string | null;
          date?: string | null;
        };
      }
    | undefined;
  qualitySignals:
    | {
        word_count?: number;
        speaker_turn_count?: number;
        unique_speaker_count?: number;
      }
    | undefined;
  overallAssessment: string | undefined;
  summaryText: string | undefined;
  strengths: Array<{ quote?: string; observation?: string; what_worked?: string; impact?: string; reflection_informed?: boolean }>;
  growthEdges: Array<{ quote?: string; moment: string; alternative_approach?: string; micro_action?: string; reflection_informed?: boolean }>;
  keyInsight: string | undefined;
  nextExperiment: string | undefined;
  encouragement: string | undefined;
  coachPrompt: string | undefined;
  evidenceMoments: Array<{ quote?: string; observation: string; whyItMatters?: string }>;
  otherBehaviorsToNotice: string[];
  coachLensSummary: string | undefined;
  frameworkAlignment: string[];
  whyThisMatters: string | undefined;
  meta: Record<string, unknown> | undefined;
  presentation: TranscriptAnalysisPresentation;
}

export function parseTranscriptAnalysisResult(
  result: unknown,
): ParsedTranscriptAnalysisResult | null {
  if (!result || typeof result !== "object") return null;

  const data = result as Record<string, unknown>;
  const meta =
    data._meta && typeof data._meta === "object"
      ? (data._meta as Record<string, unknown>)
      : undefined;
  const fallbackOverallAssessment =
    typeof data.overall_assessment === "string" ? data.overall_assessment : null;
  const fallbackKeyInsight =
    typeof data.key_insight === "string" ? data.key_insight : null;
  const fallbackNextExperiment =
    typeof data.next_experiment === "string" ? data.next_experiment : null;
  const analysisReason = deriveAnalysisReason(
    typeof meta?.analysis_reason === "string" ? meta.analysis_reason : null,
    fallbackOverallAssessment,
    fallbackKeyInsight,
    fallbackNextExperiment,
  );
  const quality = typeof data.analysis_quality === "string" ? data.analysis_quality : null;
  const transcriptDocumentContext =
    meta?.transcript_document_context && typeof meta.transcript_document_context === "object"
      ? (meta.transcript_document_context as {
          sourceFormat?: string;
          documentKind?: string;
          interactionMode?: string;
          speakerDetectionConfidence?: string;
          metadata?: {
            title?: string | null;
            date?: string | null;
          };
        })
      : undefined;
  const qualitySignals =
    meta?.quality_signals && typeof meta.quality_signals === "object"
      ? (meta.quality_signals as {
          word_count?: number;
          speaker_turn_count?: number;
          unique_speaker_count?: number;
        })
      : undefined;

  const normalizedLegacySystemFallback =
    quality === "INSUFFICIENT_DATA" && analysisReason === "ai_malformed_response";

  return {
    quality,
    analysisReason,
    transcriptDocumentContext,
    qualitySignals,
    overallAssessment: normalizedLegacySystemFallback
      ? buildSystemRetryOverallAssessment(transcriptDocumentContext, qualitySignals)
      : typeof data.overall_assessment === "string"
        ? data.overall_assessment
        : undefined,
    summaryText: normalizedLegacySystemFallback
      ? "We had enough transcript signal, but the analysis service did not complete cleanly."
      : typeof data.summary_text === "string"
        ? data.summary_text
        : undefined,
    strengths: Array.isArray(data.strengths_observed)
      ? (data.strengths_observed as Array<{ quote?: string; observation?: string; what_worked?: string; impact?: string; reflection_informed?: boolean }>)
      : [],
    growthEdges: Array.isArray(data.growth_edges)
      ? (data.growth_edges as Array<{ quote?: string; moment: string; alternative_approach?: string; micro_action?: string; reflection_informed?: boolean }>)
      : [],
    keyInsight: normalizedLegacySystemFallback
      ? "This looks like a retryable system issue, not a transcript-quality issue."
      : typeof data.key_insight === "string"
        ? data.key_insight
        : undefined,
    nextExperiment: normalizedLegacySystemFallback
      ? "Retry the analysis."
      : typeof data.next_experiment === "string"
        ? data.next_experiment
        : undefined,
    encouragement:
      typeof data.encouragement === "string" ? data.encouragement : undefined,
    coachPrompt:
      typeof data.coach_discussion_prompt === "string"
        ? data.coach_discussion_prompt
        : undefined,
    evidenceMoments: Array.isArray(data.evidence_moments)
      ? (data.evidence_moments as Array<unknown>)
          .filter((item): item is { quote?: unknown; observation: string; why_it_matters?: unknown } => (
            Boolean(item) &&
            typeof item === "object" &&
            typeof (item as { observation?: unknown }).observation === "string"
          ))
          .map((item) => ({
            quote: typeof item.quote === "string" ? item.quote : undefined,
            observation: item.observation,
            whyItMatters: typeof item.why_it_matters === "string" ? item.why_it_matters : undefined,
          }))
      : [],
    otherBehaviorsToNotice: Array.isArray(data.other_behaviors_to_notice)
      ? (data.other_behaviors_to_notice as string[]).filter((item) => typeof item === "string")
      : [],
    coachLensSummary:
      typeof data.coach_lens_summary === "string" ? data.coach_lens_summary : undefined,
    frameworkAlignment: Array.isArray(data.framework_alignment)
      ? (data.framework_alignment as string[]).filter((item) => typeof item === "string")
      : [],
    whyThisMatters:
      typeof data.why_this_matters === "string" ? data.why_this_matters : undefined,
    meta,
    presentation: getTranscriptAnalysisPresentation({
      analysisQuality: quality,
      analysisReason,
      transcriptDocumentContext,
      qualitySignals,
    }),
  };
}

function buildSystemRetryOverallAssessment(
  transcriptDocumentContext:
    | {
        sourceFormat?: string;
        documentKind?: string;
        interactionMode?: string;
        speakerDetectionConfidence?: string;
        metadata?: {
          title?: string | null;
          date?: string | null;
        };
      }
    | undefined,
  qualitySignals:
    | {
        word_count?: number;
        speaker_turn_count?: number;
        unique_speaker_count?: number;
      }
    | undefined,
): string {
  const hasUsableSignal =
    (qualitySignals?.word_count ?? 0) >= 120 ||
    (qualitySignals?.speaker_turn_count ?? 0) >= 4 ||
    transcriptDocumentContext?.interactionMode === "speaker_dominant";

  if (transcriptDocumentContext?.interactionMode === "speaker_dominant") {
    return "We had enough primary-speaker transcript signal to analyze, but the analysis service did not complete cleanly.";
  }

  if (hasUsableSignal) {
    return "We had enough transcript to analyze, but the analysis service did not complete cleanly.";
  }

  return "The analysis service did not complete cleanly. Please retry.";
}

function deriveAnalysisReason(
  explicitReason: string | null,
  overallAssessment: string | null,
  keyInsight: string | null,
  nextExperiment: string | null,
): string | null {
  if (explicitReason) return explicitReason;

  const combined = [overallAssessment, keyInsight, nextExperiment]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLowerCase();

  if (
    combined.includes("quality assessment failed") ||
    combined.includes("unable to analyze this transcript") ||
    combined.includes("analysis unavailable") ||
    combined.includes("retry upload")
  ) {
    return "ai_malformed_response";
  }

  return null;
}
